import {
  AggregatedCategoryStack,
  AggregatedData,
  AggregatedDisclosureValue,
  AggregatedOptionStackLeaf,
  AggregatedOptionStackNode,
  AggregatedValue,
  MicrositeWidgetEntity,
  WidgetType,
} from '@atlas-metrics/microsite-types/dist/types/typeIndex';
import { TFunction } from 'next-i18next';

import type { FormatValueFn } from '@/hooks/useFormatValue';

import { primaryColors } from '@/styles/colorSchemes';

import { IncompleteStatus } from '../ui/WidgetRow';

import {
  PieChartDisclosureResult,
  ProgressChartDisclosureResult,
} from '@/types';

export const hasCategoryStack = (
  value: AggregatedData
): value is AggregatedCategoryStack =>
  typeof value === 'object' &&
  ['categoryId', 'categoryName', 'options'].every((key) => key in value);

export const hasDisclosureStack = (
  value: AggregatedData
): value is AggregatedDisclosureValue[] =>
  Array.isArray(value) &&
  value.every(
    (value) =>
      typeof value === 'object' &&
      ['disclosureId', 'disclosureName', 'value'].every((key) => key in value)
  );

export const isLeaf = (
  option: AggregatedOptionStackNode | AggregatedOptionStackLeaf
): option is AggregatedOptionStackLeaf => 'value' in option;

export const getRowContent = (
  widget: MicrositeWidgetEntity,
  value: AggregatedValue,
  formatValue: FormatValueFn,
  translate: TFunction
):
  | { incompleteStatus: IncompleteStatus }
  | { value: unknown; unit?: string } => {
  if (widget.type === WidgetType.OMITTED) {
    return {
      incompleteStatus: 'OMITTED',
      value: translate('missing-data.omitted'),
    };
  }

  if (widget.type === WidgetType.MISSING_DATA) {
    return {
      incompleteStatus: 'INCOMPLETE',
      value: translate('missing-data.data-pending'),
    };
  }

  // Hide value for selection results
  if (['SINGLE_SELECT', 'MULTI_SELECT'].includes(widget.unitDataType)) {
    return {
      value: null,
    };
  }

  if (
    [
      'STRING',
      'INTEGER',
      'BOOLEAN',
      'SINGLE_SELECT',
      'MULTI_SELECT',
      'YEAR',
    ].includes(widget.unitDataType)
  ) {
    return {
      value: formatValue(value, widget.unitDataType),
    };
  }

  return {
    value: formatValue(value, widget.unitDataType),
    unit: widget.translations.unitName,
  };
};

export const getPieChartWidgetContent = (
  data: AggregatedData,
  disclosureTitle: string
) => {
  if (typeof data === 'number') {
    return [
      {
        value: data,
        name: disclosureTitle,
      },
    ];
  }

  if (hasCategoryStack(data)) {
    return data.options.map((option) => {
      if (!isLeaf(option)) {
        throw new Error('Cannot show multi grouped data yet');
      }
      return {
        name: option.optionName,
        value: option.value as number,
      };
    });
  }

  if (hasDisclosureStack(data)) {
    return data.map((disclosure) => ({
      name: disclosure.disclosureName,
      value: disclosure.value as number,
    }));
  }

  throw new Error('Unsupported data type provided');
};

export const getProgressChartWidgetContent = (
  widgetTitle: string,
  data: AggregatedData
) => {
  if (typeof data === 'number') {
    return [
      {
        name: widgetTitle,
        percentage: data,
      },
    ];
  }

  if (hasCategoryStack(data)) {
    return data.options.map((option) => {
      if (!isLeaf(option)) {
        throw new Error('Cannot show multi grouped data yet');
      }

      return {
        name: option.optionName,
        percentage: option.value as number,
      };
    });
  }

  if (hasDisclosureStack(data)) {
    return data.map((disclosure) => ({
      name: disclosure.disclosureName,
      percentage: disclosure.value as number,
    }));
  }

  throw new Error('Unsupported data type provided');
};

export const randomizeChartColorsStartIdx = (
  data: ProgressChartDisclosureResult[] | PieChartDisclosureResult[]
) => ((data[0]?.name?.length ?? 0) + 1) % primaryColors.length;
